import React, { useRef, useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import {
  Avatar,
  Box,
  ButtonBase,
  Hidden,
  Popover,
  Typography,
  makeStyles,
  List,
  ListItem,
  SvgIcon,
  ListItemAvatar,
  ListItemText
} from '@material-ui/core';
import useAuth from 'src/hooks/useAuth';
import {
  User as UserIcon,
  Disc as ActivityIcon,
  LogOut as LogOutIcon,
  Sliders as PermissionsIcon
} from 'react-feather';
import {
  ArrowDropDown,
  ArrowDropUp,
  AccountBalanceWallet as CashIcon
} from '@material-ui/icons';
import { useDispatch } from 'src/store';
import { logout } from 'src/slices/auth';
import { usernameWidth } from 'src/slices/toggle';
import NetworkBadge from 'src/components/NetworkAvatar';
import { currencyFormatter } from 'src/utils/formatter';
import getInitials from 'src/utils/getInitials';

const useStyles = makeStyles((theme) => ({
  avatar: {
    height: 32,
    width: 32
  },
  popover: {
    width: 220
  },
  icon: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText
  }
}));

const Account = () => {
  const classes = useStyles();
  const history = useHistory();
  const ref = useRef(null);
  const [isOpen, setOpen] = useState(false);

  const usernameRef = useRef();

  const { user } = useAuth();
  const dispatch = useDispatch();

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleLogout = async () => {
    try {
      handleClose();
      await dispatch(logout());
      history.push('/');
    } catch (err) {
      console.error(err);
    }
  };

  // Checks for username length in px
  useEffect(() => {
    if (usernameRef.current)
      dispatch(usernameWidth(usernameRef.current.clientWidth));
  });

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        component={ButtonBase}
        onClick={handleOpen}
        ref={ref}
      >
        <NetworkBadge>
          <Avatar src={user.userProfilePicture} className={classes.avatar} />
        </NetworkBadge>
        <Box mr={1} />
        <Hidden smDown>
          <Typography variant="h5" color="inherit" ref={usernameRef}>
            {user.userFirstName} {getInitials(user.userLastName)}.
          </Typography>
        </Hidden>
        {!isOpen ? <ArrowDropDown /> : <ArrowDropUp />}
      </Box>

      <Popover
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        classes={{ paper: classes.popover }}
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
      >
        <List disablePadding onClick={handleClose}>
          <ListItem component={RouterLink} to="/app/settings/profile">
            <ListItemAvatar>
              <Avatar className={classes.icon}>
                <SvgIcon fontSize="small">
                  <UserIcon />
                </SvgIcon>
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={'Profile'}
              primaryTypographyProps={{
                variant: 'subtitle2',
                color: 'textPrimary'
              }}
            />
          </ListItem>
          <ListItem component={RouterLink} to="/app/buy-credit">
            <ListItemAvatar>
              <Avatar className={classes.icon}>
                <SvgIcon fontSize="small">
                  <CashIcon />
                </SvgIcon>
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={`My credit: ${`${currencyFormatter(
                user.userAccountBalance
              )}`}`}
              primaryTypographyProps={{
                variant: 'subtitle2',
                color: 'textPrimary'
              }}
            />
          </ListItem>
          <ListItem component={RouterLink} to="/app/permissions">
            <ListItemAvatar>
              <Avatar className={classes.icon}>
                <SvgIcon fontSize="small">
                  <PermissionsIcon />
                </SvgIcon>
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={'Permissions'}
              primaryTypographyProps={{
                variant: 'subtitle2',
                color: 'textPrimary'
              }}
            />
          </ListItem>
          <ListItem component={RouterLink} to="/app/settings/audit">
            <ListItemAvatar>
              <Avatar className={classes.icon}>
                <SvgIcon fontSize="small">
                  <ActivityIcon />
                </SvgIcon>
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={'Audit'}
              primaryTypographyProps={{
                variant: 'subtitle2',
                color: 'textPrimary'
              }}
            />
          </ListItem>
          <ListItem component={RouterLink} to="#" onClick={handleLogout}>
            <ListItemAvatar>
              <Avatar className={classes.icon}>
                <SvgIcon fontSize="small">
                  <LogOutIcon />
                </SvgIcon>
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={'Logout'}
              primaryTypographyProps={{
                variant: 'subtitle2',
                color: 'textPrimary'
              }}
            />
          </ListItem>
        </List>
      </Popover>
    </>
  );
};

export default Account;
